import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { dataService } from '@/services/dataService';
import { MemoryEntry } from '@/types/entities';
import { EntityDrawer } from '@/components/ui/EntityDrawer';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { useI18n } from '@/i18n';
import { useToast } from '@/components/ui/Toast';
import { MemoryFormDrawer, MemoryFormValues, MEMORY_DOMAINS } from './MemoryFormDrawer';

function splitTags(raw: string): string[] {
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * SHARED MEMORY ("Ugarits Brain") — Phase D completion. Local Preview
 * records only; search is plain substring matching over
 * title/content/tags (dataService.searchMemory), never claimed as
 * semantic/AI search. Full CRUD proxies to dataService.
 */
export function MemoryPage() {
  const { t } = useI18n();
  const push = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [query, setQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [selected, setSelected] = useState<MemoryEntry | null>(null);
  const [formOpen, setFormOpen] = useState(searchParams.get('new') === '1');
  const [editTarget, setEditTarget] = useState<MemoryEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MemoryEntry | null>(null);

  function reload() {
    if (query.trim() || domainFilter !== 'all') {
      dataService.searchMemory(query, domainFilter === 'all' ? undefined : domainFilter).then(setEntries);
    } else {
      dataService.listMemory().then(setEntries);
    }
  }
  useEffect(reload, [query, domainFilter]);

  async function handleCreate(values: MemoryFormValues) {
    const created = await dataService.createMemory({ ...values, tags: splitTags(values.tags) });
    push(`${t('toast.memoryAdded')} — ${created.title}`, 'success');
    setFormOpen(false);
    setSearchParams({});
    reload();
  }
  async function handleEdit(values: MemoryFormValues) {
    if (!editTarget) return;
    await dataService.updateMemory(editTarget.id, { ...values, tags: splitTags(values.tags) });
    push(t('toast.memoryUpdated'), 'success');
    setEditTarget(null);
    setSelected(null);
    reload();
  }
  async function archive(m: MemoryEntry) {
    await dataService.archiveMemory(m.id);
    push(t('toast.memoryArchived'), 'warn');
    setSelected(null);
    reload();
  }
  async function confirmDelete() {
    if (!deleteTarget) return;
    await dataService.deleteMemory(deleteTarget.id);
    push(t('toast.demoRecordDeleted'), 'error');
    setDeleteTarget(null);
    setSelected(null);
    reload();
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('nav.memory')}</h1>
        <button className="btn-primary" onClick={() => setFormOpen(true)}>+ {t('memoryForm.newTitle')}</button>
      </div>
      <div className="soon-tag-inline" style={{ marginBottom: 6, display: 'inline-block' }}>{t('memory.badge')}</div>
      <p style={{ color: 'var(--text-2)', marginTop: 4, marginBottom: 16 }}>{t('memory.subtitle')}</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          className="text-input"
          placeholder={t('memory.searchPlaceholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ width: 260 }}
        />
        <select className="text-input" style={{ width: 200 }} value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)}>
          <option value="all">{t('memory.allDomains')}</option>
          {MEMORY_DOMAINS.map((d) => <option key={d} value={d}>{t(`memoryDomain.${d}`)}</option>)}
        </select>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{t('memory.plainTextSearchNote')}</span>
      </div>

      {entries.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>{t('memory.empty')}</div>
      ) : (
        <div className="agent-node-grid">
          {entries.map((m) => (
            <div key={m.id} className="agent-node" style={{ cursor: 'pointer' }} onClick={() => setSelected(m)}>
              <div className="agent-node-head">
                <div>
                  <div className="agent-node-name">{m.title}</div>
                  <div className="agent-node-tagline">{t(`memoryDomain.${m.domain}`)}</div>
                </div>
                <span className={`badge ${m.visibility === 'team' ? 'info' : 'neutral'}`}>{t(`memoryForm.visibility${m.visibility === 'team' ? 'Team' : 'Private'}`)}</span>
              </div>
              <div className="agent-node-meta">
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 6 }}>{m.content.slice(0, 140)}{m.content.length > 140 ? '…' : ''}</div>
                {m.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
                    {m.tags.map((tag) => <span key={tag} className="soon-tag-inline">{tag}</span>)}
                  </div>
                )}
                <div className="agent-node-meta-row"><span>{t('memory.agentAccessCount')}</span><span>{m.agentAccess.length}</span></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <EntityDrawer title={selected.title} subtitle={t(`memoryDomain.${selected.domain}`)} onClose={() => setSelected(null)}>
          <p style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{selected.content}</p>
          {selected.source && <p style={{ fontSize: 12, color: 'var(--text-3)' }}><b>{t('memoryForm.source')}:</b> {selected.source}</p>}
          <p style={{ fontSize: 12, color: 'var(--text-3)' }}><b>{t('memory.agentAccessCount')}:</b> {selected.agentAccess.length}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn-primary" onClick={() => { setEditTarget(selected); setSelected(null); }}>{t('rowMenu.edit')}</button>
            <button style={{ color: 'var(--warn)' }} onClick={() => archive(selected)}>{t('rowMenu.archive')}</button>
            {selected.isPreviewCreated && <button style={{ color: 'var(--crit)' }} onClick={() => setDeleteTarget(selected)}>{t('rowMenu.deleteDemo')}</button>}
          </div>
        </EntityDrawer>
      )}

      {formOpen && <MemoryFormDrawer onClose={() => { setFormOpen(false); setSearchParams({}); }} onSubmit={handleCreate} />}
      {editTarget && <MemoryFormDrawer entry={editTarget} onClose={() => setEditTarget(null)} onSubmit={handleEdit} />}

      <ConfirmModal
        open={!!deleteTarget}
        title={t('confirm.deleteMemoryTitle')}
        recordName={deleteTarget?.title}
        description={t('confirm.deleteMemoryDesc')}
        reversible={false}
        tone="destructive"
        confirmLabel={t('rowMenu.deleteDemo')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
